import GameSprite from "./game-sprite";

/**
 * Instance Classes are used in Gamefroot to represent game instances
 * with common origin.
 * 
 * This class tracks the origin template, and all instances created from
 * the template. When destroyed, these instances are disabled and stored.
 * They can be reinstantiated from the pool.
 * This will save on creation/destruction.
 * 
 * @class InstanceClass
 */
const InstanceClass = class {
  /**
   * @param {Phaserfroot.Level} scene - where instances are created
   * @param {Object} template - data used to construct instances
   */
  constructor ( scene, template ) {

    /**
     * Instances being used in the game world.
     * @type {Array}
     */
    this.instances = [];

    /**
     * Instances not being used in the game world.
     * @type {Array}
     */
    this.pool = [];

    /**
     * Scene in which this InstanceClass operates.
     * @type {Phaserfroot.Level}
     */
    this.scene = scene;

    /**
     * Data used to construct instances of this InstanceClass.
     * @type {Object}
     */
    this.template = template;
  }

  /**
   * Create an instance of this class.
   * @returns {Phaserfroot.GameSprite}
   */
  create () {
    const instance = new GameSprite( this.scene, this.template );
    this.instances.push( instance );
    return instance;
  }

  /**
   * Destroy part of the pool to relieve system pressure.
   * 
   * Pool objects still exist in the scene, and are checked by various
   * processes. This cost is offset by the saved cost of running
   * create and destroy methods.
   * 
   * Create/destroy savings are constant per object.
   * Existence costs are much smaller, but ongoing per object.
   * We need simply destroy fewer objects than would offset the savings.
   */
  drain () {
    const rate = Math.round( ( this.pool.length - 128 ) / 32 );
    for ( let i = 0; i < rate; i++ ) {
      const instance = this.pool.pop();
      instance.destroy();
    }
  }

  /**
   * Get an instance, either by creating or by reinstantiating from the pool.
   * @returns {Phaserfroot.GameSprite}
   */
  get () {
    if ( this.pool.length ) {
      return this.reinstantiate();
    }
    return this.create();
  }

  /**
   * Deactivate an instance and send it to the pool.
   * @param {Phaserfroot.GameSprite} instance - instance to remove
   */
  kill ( instance ) {
    instance.visible = false;

    // This will trigger `destroy` on components.
    instance.components.removeAll();

    const index = this.instances.indexOf( instance );
    if ( index > -1 ) {
      this.instances.splice( index, 1 );
      this.pool.push( instance );
    } else {
      console.warn( "Instance is not ready, cannot send to pool:", instance );
    }
  }

  /**
   * Pull an object from the pool and make it active again.
   * @returns {Phaserfroot.GameSprite}
   */
  reinstantiate () {
    const instance = this.pool.pop();
    this.instances.push( instance );
    instance.active = true;
    instance.alpha = 1;
    instance.exists = true;
    instance.visible = true;

    instance.posX = this.template.position.x;
    instance.posY = this.template.position.y;
    instance.bearing = this.template.rotation;
    instance.scaleX = this.template.scale.x;
    instance.scaleY = this.template.scale.y;
    instance.anchorX = this.template.anchor.x;
    instance.anchorY = this.template.anchor.y;

    if ( instance.playMy ) {
      // If "idle" does not exist, and the object was playing a
      // non-default animation, it will continue playing that animation.
      instance.playMy( "idle" );
    }

    if ( instance.setPhysics ) {
      instance.setPhysics( true );
      instance.body.reset( instance.x, instance.y );
    }
    this.scene.physicsLayersManager.addToLayer( instance, 0 );
    instance.setupPluginComponents( this.template );

    const children = this.scene.getChildren();
    this.scene.addChildAfter( instance, children[ children.length - 1 ] );

    return instance;
  }
};

export default InstanceClass;
